import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ArrowUpRight, Mail, Globe, Phone, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { ORG } from "@/data/tools";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { FeedbackForm, ImpactForm, ContactForm } from "@/components/public/Forms";

const Page = ({ eyebrow, title, accent, intro, testid, children, width = "max-w-3xl" }) => (
  <>
    <TopBar minimal />
    <main id="main" tabIndex={-1} className="outline-none">
      <article className={`mx-auto ${width} px-5 pb-20 pt-[150px] sm:px-8 lg:pt-[178px]`} aria-labelledby={`${testid}-title`} data-testid={testid}>
        <p className="eyebrow mb-4">{eyebrow}</p>
        <h1 id={`${testid}-title`} className="font-display text-[2.6rem] font-medium leading-[1] tracking-[-0.03em] sm:text-6xl">{title} {accent && <span className="italic text-brand">{accent}</span>}</h1>
        {intro && <p className="mt-6 text-lg leading-relaxed text-ink-soft">{intro}</p>}
        <div className="mt-10">{children}</div>
      </article>
    </main>
    <Footer />
  </>
);

const Prose = ({ sections }) => sections.map(([h, p]) => (
  <section key={h} className="mt-8 first:mt-0"><h2 className="font-display text-2xl font-medium sm:text-3xl">{h}</h2><p className="mt-3 leading-relaxed">{p}</p></section>
));

export const FeedbackPage = () => (
  <Page eyebrow="Feedback initiative" title="Tell us what" accent="helped" testid="feedback-page" intro="Your views on the SENDCO Quick Start & Improvement Workbook decide what we improve and build next. Every question is optional.">
    <FeedbackForm />
  </Page>
);

export const ImpactPage = () => (
  <Page eyebrow="One month on" title="One-month" accent="impact review" testid="impact-page" intro="About a month after using the workbook, tell us what changed. Your review helps other schools and shapes future resources.">
    <ImpactForm />
  </Page>
);

const Card = ({ r }) => {
  const internal = r.url?.startsWith("/");
  const soon = r.status === "coming-soon" || !r.url;
  const body = (
    <>
      <span className="eyebrow">{r.kind === "external" ? "Partner resource" : r.kind}</span>
      <span className="mt-2 block font-display text-2xl font-medium">{r.name}</span>
      <span className="mt-2 block text-sm text-ink-soft">{r.description}</span>
      <span className="mt-4 flex items-center gap-2 font-bold">
        {soon ? <><Clock size={16} aria-hidden /> {r.status === "coming-soon" ? "Coming soon" : "Link coming soon"}</> : internal ? <>Open <ArrowUpRight size={16} aria-hidden /></> : <>Visit <ExternalLink size={16} aria-hidden /><span className="sr-only">(opens in a new tab)</span></>}
      </span>
    </>
  );
  const cls = "block h-full rounded-[1.5rem] border-2 border-line bg-surface p-6 transition-colors hover:border-brand";
  if (soon) return <div className={`${cls} hover:border-line`} data-testid={`resource-${r.slug}`}>{body}</div>;
  return internal ? <Link to={r.url} className={cls} data-testid={`resource-${r.slug}`}>{body}</Link>
    : <a href={r.url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`resource-${r.slug}`}>{body}</a>;
};

export const ResourcesPage = () => {
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/resources").then(({ data }) => setList(Array.isArray(data) ? data : [])).catch(() => setList([]));
  }, []);
  return (
    <Page eyebrow="Resource directory" title="Resources and" accent="toolkits" testid="resources-page" width="max-w-[1100px]" intro="Everything from The SEND Accountability Project in one place. Partner resources open in a new tab.">
      {list === null ? <p role="status">Loading…</p> : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{list.map((r) => <li key={r.id}><Card r={r} /></li>)}</ul>
      )}
    </Page>
  );
};

export const ContactPage = () => (
  <Page eyebrow="Contact us" title="Get in" accent="touch" testid="contact-page" intro="Questions about the workbook, toolkits or data protection? Send us a message or contact us directly.">
    <ContactForm />
    <h2 className="mb-4 mt-12 font-display text-2xl font-medium sm:text-3xl">Other ways to reach us</h2>
    <ul className="space-y-4 text-lg [&>li]:flex-wrap [&_a]:break-all">
      <li className="flex items-center gap-3"><Mail className="text-brand" aria-hidden /> Director: <a className="font-bold underline" href={`mailto:${ORG.director}`} data-testid="contact-director">{ORG.director}</a></li>
      <li className="flex items-center gap-3"><Mail className="text-brand" aria-hidden /> Data lead: <a className="font-bold underline" href={`mailto:${ORG.datalead}`} data-testid="contact-datalead">{ORG.datalead}</a></li>
      <li className="flex items-center gap-3"><Phone className="text-brand" aria-hidden /> <a className="font-bold underline" href={`tel:${ORG.phone.replace(/\s/g, "")}`}>{ORG.phone}</a></li>
      <li className="flex items-center gap-3"><Globe className="text-brand" aria-hidden /> SEND Family Hub: <a className="font-bold underline" href={`https://${ORG.web}`} target="_blank" rel="noopener noreferrer">{ORG.web}</a></li>
    </ul>
    <p className="mt-8 text-ink-soft">{ORG.name} · {ORG.office} · Company Number {ORG.company} · ICO Registration {ORG.ico}</p>
    <p className="mt-4 text-sm text-ink-soft">Please don't send any pupil information by email.</p>
  </Page>
);

export const AccessibilityPage = () => (
  <Page eyebrow="Accessibility statement" title="Designed for" accent="everyone" testid="accessibility-page">
    <Prose sections={[
      ["Our aim", "We aim to meet the Web Content Accessibility Guidelines (WCAG) 2.2 at level AA across this website and our downloadable workbooks."],
      ["What you can do", "Use the accessibility controls in the top bar to enlarge text, switch to a dyslexia-friendly font, turn on high contrast, reduce motion and have the page read aloud. Every form can be completed with a keyboard and a screen reader, and layouts work on phones and tablets."],
      ["PDFs and printing", "Workbooks print with clear headings, high contrast and a consistent footer. Save as PDF from your browser's print window to keep a copy."],
      ["Plain English", "We write in plain English and explain any specialist terms."],
      ["Tell us about a problem", `If anything is hard to use, email ${ORG.director} and we'll fix it or send you the content in another format.`],
    ]} />
  </Page>
);

export const DisclaimerPage = () => (
  <Page eyebrow="Compliance disclaimer" title="How to use" accent="these tools" testid="disclaimer-page">
    <Prose sections={[
      ["Reflection and planning", "Our workbooks and toolkits support reflection, self-review and improvement planning in schools."],
      ["Not legal advice", "They are not legal advice and not consultancy. Always check the official legislation and statutory guidance, and take professional advice where needed."],
      ["Not evidence of compliance", "Completing a workbook or toolkit is not, on its own, evidence of statutory compliance."],
      ["Pupil information", "Use pseudonymised references such as Pupil A001. Never record names, dates of birth, NHS numbers, addresses or other details that identify a child."],
    ]} />
  </Page>
);

const FUTURE = ["SEND Dashboard Suite", "EHCP Tracker", "Improvement Planning Hub", "Trust and MAT dashboards", "CPD platform", "Legislative Intelligence Hub", "AI-assisted planning tools", "School Workspace System", "Collaborative improvement planning",
  "Policy Alignment Generator", "Sensory & Communication Environmental Audit", "Teacher–TA Co-Production Tracker", "SEND pathway frameworks (SLCN, SEMH, Cognition & Learning, Sensory & Physical)", "Transition planning frameworks"];

export const FuturePage = () => (
  <Page eyebrow="Future developments" title="What we're" accent="building next" testid="future-page" intro="Our roadmap is shaped by your feedback and one-month reviews. Tell us what matters most to your school.">
    <ul className="grid gap-3 sm:grid-cols-2">{FUTURE.map((f) => <li key={f} className="rounded-2xl border-2 border-line bg-surface p-4 font-bold">{f}</li>)}</ul>
    <div className="mt-8 flex flex-wrap gap-3">
      <Link to="/feedback" className="btn btn-brand" data-testid="future-feedback">Request a feature</Link>
      <Link to="/resource-centre" className="btn btn-ghost" data-testid="future-workspace">Try the school workspace</Link>
    </div>
  </Page>
);
